import React from 'react';

// Parse custom formatting tags {{bold}}, {{code}}, and markdown code blocks (```yaml, ```bash, etc.)
export function parseFormattedText(text: string): React.ReactNode {
  if (!text) return null;

  // Ensure text is a string
  if (typeof text !== 'string') {
    console.warn('parseFormattedText received non-string:', typeof text, text);
    return String(text);
  }

  const parts: React.ReactNode[] = [];
  let key = 0;

  // First, extract and replace markdown code blocks with placeholders
  const codeBlockPlaceholders: { [key: string]: { language: string; code: string } } = {};
  let codeBlockIndex = 0;

  // Match ```language\n...code...\n``` blocks
  const codeBlockRegex = /```(\w+)?\s*\n([\s\S]*?)```/g;
  let textWithPlaceholders = text.replace(codeBlockRegex, (match, language, code) => {
    const placeholder = `__CODE_BLOCK_${codeBlockIndex}__`;
    codeBlockPlaceholders[placeholder] = {
      language: language || 'text',
      code: code.trim()
    };
    codeBlockIndex++;
    return placeholder;
  });

  // Clean up malformed custom tags
  let cleanedText = textWithPlaceholders;
  cleanedText = cleanedText.replace(/\{\{\/code\}(?!\})/g, '{{/code}}');
  cleanedText = cleanedText.replace(/\{\{\/bold\}(?!\})/g, '{{/bold}}');

  // Remove orphaned closing tags
  let previousText = '';
  let iterations = 0;
  const maxIterations = 10;

  while (cleanedText !== previousText && iterations < maxIterations) {
    previousText = cleanedText;

    const lines = cleanedText.split('\n');
    const cleanedLines = lines.map(line => {
      // Skip lines with code block placeholders
      if (line.includes('__CODE_BLOCK_')) {
        return line;
      }

      const openCount = (line.match(/\{\{code\}\}/g) || []).length;
      const closeCount = (line.match(/\{\{\/code\}\}/g) || []).length;

      if (closeCount > openCount) {
        const excess = closeCount - openCount;
        for (let i = 0; i < excess; i++) {
          line = line.replace(/\{\{\/code\}\}/, '');
        }
      }

      const boldOpenCount = (line.match(/\{\{bold\}\}/g) || []).length;
      const boldCloseCount = (line.match(/\{\{\/bold\}\}/g) || []).length;

      if (boldCloseCount > boldOpenCount) {
        const excessBold = boldCloseCount - boldOpenCount;
        for (let i = 0; i < excessBold; i++) {
          line = line.replace(/\{\{\/bold\}\}/, '');
        }
      }

      return line;
    });

    cleanedText = cleanedLines.join('\n');
    iterations++;
  }

  // Convert markdown inline code (`...`) to {{code}}...{{/code}} before parsing
  // This must happen after code block extraction to avoid matching backticks inside code blocks
  cleanedText = cleanedText.replace(/`([^`\n]+)`/g, '{{code}}$1{{/code}}');

  let currentIndex = 0;

  // Parse custom {{bold}} and {{code}} tags
  const customTagRegex = /\{\{(bold|code)\}\}(.*?)\{\{\/(bold|code)\}\}/g;
  let match;

  while ((match = customTagRegex.exec(cleanedText)) !== null) {
    if (match[1] === match[3]) {
      // Add text before the match
      if (match.index > currentIndex) {
        const textBefore = cleanedText.substring(currentIndex, match.index);
        if (textBefore) {
          // Process the text before for code block placeholders
          parts.push(...processTextWithCodeBlocks(textBefore, codeBlockPlaceholders, key));
          key += 100; // Reserve space for code block keys
        }
      }

      const type = match[1];
      const content = match[2];

      if (type === 'bold') {
        parts.push(<strong key={key++} className="font-semibold text-foreground">{content}</strong>);
      } else if (type === 'code') {
        parts.push(<code key={key++} className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground">{content}</code>);
      }

      currentIndex = match.index + match[0].length;
    }
  }

  // Add remaining text after all matches
  if (currentIndex < cleanedText.length) {
    const remainingText = cleanedText.substring(currentIndex);
    if (remainingText) {
      parts.push(...processTextWithCodeBlocks(remainingText, codeBlockPlaceholders, key));
    }
  }

  return parts.length > 0 ? <>{parts}</> : cleanedText;
}

// Helper function to process text and replace code block placeholders with rendered code blocks
function processTextWithCodeBlocks(text: string, placeholders: { [key: string]: { language: string; code: string } }, startKey: number): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let key = startKey;
  let currentIndex = 0;

  // Find all code block placeholders in the text
  const placeholderRegex = /__CODE_BLOCK_(\d+)__/g;
  let match;

  while ((match = placeholderRegex.exec(text)) !== null) {
    // Add text before the placeholder
    if (match.index > currentIndex) {
      const textBefore = text.substring(currentIndex, match.index);
      if (textBefore.trim()) {
        parts.push(textBefore);
      }
    }

    // Add the code block
    const placeholder = match[0];
    const codeBlockData = placeholders[placeholder];

    if (codeBlockData) {
      parts.push(
        <pre key={key++} className="my-3 bg-muted p-3 rounded-md border font-mono text-xs overflow-x-auto">
          <code className="text-foreground">{codeBlockData.code}</code>
        </pre>
      );
    }

    currentIndex = match.index + match[0].length;
  }

  // Add remaining text after all placeholders
  if (currentIndex < text.length) {
    const remainingText = text.substring(currentIndex);
    if (remainingText.trim()) {
      parts.push(remainingText);
    }
  }

  // If no placeholders were found, just return the text
  if (parts.length === 0 && text.trim()) {
    parts.push(text);
  }

  return parts;
}
